"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ArrowUp, CheckCheck } from "lucide-react"
import { InformationCircle } from "@medusajs/icons"
import { useTranslations, useLocale } from "next-intl"

import type {
  Conversation,
  ConversationMessage,
  MessageActor,
} from "@lib/data/conversations"
import {
  getConversationWithMessages,
  sendMessage as dataSendMessage,
} from "@lib/data/conversations"

import {
  Message,
  MessageAvatar,
  MessageContent,
  MessageFooter,
} from "@/components/ui/message"
import { Bubble, BubbleContent } from "@/components/ui/bubble"
import { Marker, MarkerContent } from "@/components/ui/marker"
import { ChatButton } from "@/components/ui/chat-button"

type Props = {
  conversation: Conversation
  initialMessages: ConversationMessage[]
  locale?: string
  /** Called after a message is sent successfully, e.g. to refresh the list. */
  onSent?: () => void
}

type Item =
  | { kind: "day"; key: string; label: string }
  | {
      kind: "message"
      key: string
      message: ConversationMessage
      mine: boolean
      read: boolean
      isLast: boolean
    }

const dayKey = (iso: string) => new Date(iso).toDateString()

const dayLabel = (
  iso: string,
  locale: string,
  t: ReturnType<typeof useTranslations>
) => {
  const d = new Date(iso)
  const today = new Date().toDateString()
  const yesterday = new Date(Date.now() - 86_400_000).toDateString()
  try {
    if (dayKey(iso) === today) return t("today")
    if (dayKey(iso) === yesterday) return t("yesterday")
    return d.toLocaleDateString(locale, { day: "numeric", month: "long" })
  } catch {
    return ""
  }
}

const formatTime = (iso: string, locale: string) => {
  try {
    return new Date(iso).toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return ""
  }
}

const avatarText = (
  actor: MessageActor,
  sellerName: string | null | undefined,
  t: ReturnType<typeof useTranslations>
) => {
  if (actor === "customer") return t("you").charAt(0).toUpperCase()
  if (actor === "seller" && sellerName) {
    return sellerName.charAt(0).toUpperCase()
  }
  return t(`actor.${actor}` as Parameters<typeof t>[0]).charAt(0).toUpperCase()
}

const MessageThread = ({ conversation, initialMessages, onSent }: Props) => {
  const t = useTranslations("AccountMessages")
  const locale = useLocale()
  const [messages, setMessages] =
    useState<ConversationMessage[]>(initialMessages)
  const [draft, setDraft] = useState("")
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const draftRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  // Whether the reader is at (or near) the live edge. Only then do we pin
  // the view to the newest message; scrolled-up readers keep their place.
  const stickRef = useRef(true)

  const items = useMemo<Item[]>(() => {
    const other = conversation.participants?.find(
      (p) => p.actor_type !== "customer"
    )
    const otherLastRead = other?.last_read_at
      ? new Date(other.last_read_at)
      : null
    const list: Item[] = []
    let lastDay = ""

    messages.forEach((msg, index) => {
      const day = dayKey(msg.created_at)
      if (day !== lastDay) {
        lastDay = day
        list.push({
          kind: "day",
          key: `day-${day}`,
          label: dayLabel(msg.created_at, locale, t),
        })
      }
      const mine = msg.author_actor_type === "customer"
      const read =
        mine &&
        otherLastRead !== null &&
        new Date(msg.created_at) <= otherLastRead
      list.push({
        kind: "message",
        key: msg.id,
        message: msg,
        mine,
        read,
        isLast: index === messages.length - 1,
      })
    })

    return list
  }, [messages, conversation.participants, locale, t])

  // SSE live updates from the backend stream. The plugin emits named
  // `message.created` events whose payload carries only the message id and
  // author (no body or timestamp), so every event triggers a refetch of
  // the conversation's messages and a merge by id.
  useEffect(() => {
    let es: EventSource | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let closed = false

    const refresh = async () => {
      const data = await getConversationWithMessages(conversation.id)
      if (!data) return
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id))
        const fresh = data.messages.filter((m) => !seen.has(m.id))
        return fresh.length ? [...prev, ...fresh] : prev
      })
    }

    const connect = () => {
      if (closed) return
      es = new EventSource(`/api/conversation-stream/${conversation.id}`)
      es.addEventListener("message.created", () => {
        void refresh()
      })
      es.onerror = () => {
        es?.close()
        es = null
        if (!closed) {
          reconnectTimer = setTimeout(connect, 5000)
        }
      }
    }

    connect()

    return () => {
      closed = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      es?.close()
    }
  }, [conversation.id])

  const handleSend = useCallback(async () => {
    const body = draft.trim()
    if (!body || sending) return

    setSending(true)
    setError(null)

    // Optimistic update
    const optimistic: ConversationMessage = {
      id: `opt-${Date.now()}`,
      conversation_id: conversation.id,
      author_actor_type: "customer",
      author_actor_id: "",
      body,
      created_at: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, optimistic])
    setDraft("")

    const sent = await dataSendMessage(conversation.id, body)
    setSending(false)

    if (!sent) {
      // Roll back optimistic message
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id))
      setError(t("sendError"))
      return
    }

    // Replace optimistic with real
    setMessages((prev) =>
      prev.map((m) => (m.id === optimistic.id ? sent : m))
    )
    onSent?.()
  }, [draft, sending, conversation.id, t, onSent])

  const canSend = draft.trim().length > 0 && !sending

  // Pin to the newest message whenever the list changes and the reader
  // has not scrolled away. This replaces the shadcn message-scroller,
  // whose turn anchoring and spacer are built for AI chats and pushed
  // messages to the top of the viewport instead of the bottom.
  useEffect(() => {
    const el = scrollRef.current
    if (el && stickRef.current) {
      el.scrollTop = el.scrollHeight
    }
  }, [items])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }, [])

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        aria-label={t("heading")}
        data-testid="message-thread-scroller"
        className="min-h-0 flex-1 overflow-y-auto"
      >
        <div className="flex min-h-full flex-col justify-end gap-6 px-4 py-4">
          {items.map((item) =>
            item.kind === "day" ? (
              <Marker key={item.key} variant="separator" className="text-xs">
                <MarkerContent>{item.label}</MarkerContent>
              </Marker>
            ) : (
              <Message
                key={item.key}
                align={item.mine ? "end" : "start"}
                className="w-auto max-w-[85%]"
              >
                <MessageAvatar
                  className={
                    item.mine
                      ? "size-8 bg-primary text-xs font-medium text-primary-foreground"
                      : "size-8 bg-muted text-xs font-medium text-muted-foreground"
                  }
                >
                  {avatarText(
                    item.message.author_actor_type,
                    conversation.seller_name,
                    t
                  )}
                </MessageAvatar>
                <MessageContent className="min-w-0">
                  <Bubble
                    variant={item.mine ? "default" : "secondary"}
                    className="max-w-full"
                  >
                    <BubbleContent className="whitespace-pre-wrap">
                      {item.message.body}
                    </BubbleContent>
                  </Bubble>
                  <MessageFooter>
                    {formatTime(item.message.created_at, locale)}
                    {item.mine && item.read && (
                      <CheckCheck
                        aria-hidden="true"
                        className="size-3.5 shrink-0"
                      />
                    )}
                  </MessageFooter>
                </MessageContent>
              </Message>
            )
          )}
        </div>
      </div>

      {items.length === 0 && (
        <p className="txt-small py-8 text-center text-ui-fg-subtle">
          {t("emptyThread")}
        </p>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 pb-2 text-small text-ui-fg-danger">
          <InformationCircle width={16} height={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Reply input, styled after the shadcn Prompt Input: a bordered,
          rounded group holding the textarea with a bottom addon row that
          carries the round send button. */}
      <div className="p-3">
        <div className="rounded-2xl border border-ui-border-base bg-muted/30 focus-within:border-ui-border-strong">
          <textarea
            className="max-h-40 min-h-14 w-full resize-none bg-transparent px-3.5 py-3 txt-small text-ui-fg-base placeholder:text-ui-fg-subtle focus:outline-none"
            rows={2}
            placeholder={t("replyPlaceholder")}
            value={draft}
            ref={draftRef}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            disabled={sending}
          />
          <div className="flex items-center justify-end px-2.5 pb-2.5">
            <ChatButton
              type="button"
              size="icon-sm"
              className="size-8 rounded-full"
              onClick={handleSend}
              disabled={!canSend}
              aria-label={t("sendReply")}
              data-testid="send-message-button"
            >
              <ArrowUp aria-hidden="true" className="size-4" />
            </ChatButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MessageThread
